import TextQuestionSchema from '@ax2/common/model/schemas/TextQuestionSchema';
import {
  QuestionChangeData,
  QuestionRandomizeData,
  QuestionPDAnswerTypeData,
  QuestionNAAnswerTypeData,
} from 'components/question-builders/QuestionContent';
import GridQuestionSchema from '@ax2/common/model/schemas/GridQuestionSchema';
import { replaceById, add } from '@ax2/common/helpers/array';
import _ from 'lodash';
import {
  createAnswerSchema,
  createAnswerSchemaWithLabel,
} from './questionSchemaCreator';
import { GridOption } from 'components/question-builders/base/Grid';
import ListQuestionSchema from '@ax2/common/model/schemas/ListQuestionSchema';
import { ListOption } from 'components/question-builders/base/List';
import { OpenEndedTextQuestionData } from 'components/question-builders/OpenEndedTextQuestion';
import { OpenEndedNumericQuestionData } from 'components/question-builders/OpenEndedNumericQuestion';
import { getTemplateData } from './templateDataConverter';
import AnswerColumnSchema from '@ax2/common/model/schemas/AnswerColumnSchema';
import AnswerRowSchema from '@ax2/common/model/schemas/AnswerRowSchema';
import {
  StepOption,
  AttrOption,
} from 'components/question-builders/base/Scale';
import { CheckboxGridQuestionData } from 'components/question-builders/CheckboxGridQuestion';
import { RatingListQuestionData } from 'components/question-builders/RatingListQuestion';
import { TextboxListSumData } from 'components/question-builders/TextboxListSumQuestion';
import EmptyQuestionSchema from '@ax2/common/model/schemas/EmptyQuestionSchema';
import { EmptyQuestionData } from 'components/question-builders/EmptyQuestion';
import QuestionType from '@ax2/common/model/QuestionType';
import { TextboxListTextQuestionData } from 'components/question-builders/TextboxListTextQuestion';
import { TextboxListNumericQuestionData } from 'components/question-builders/TextboxListNumericQuestion';
import { DropdownMenuQuestionData } from 'components/question-builders/DropdownMenuQuestion';
import { CheckboxListQuestionData } from 'components/question-builders/CheckboxListQuestion';
import { RadioButtonListQuestionData } from 'components/question-builders/RadioButtonListQuestion';
import { RadioButtonGridQuestionData } from 'components/question-builders/RadioButtonGridQuestion';
import { RadioButtonGridRowQuestionData } from 'components/question-builders/RadioButtonGridRowQuestion';
import { TextboxGridNumericQuestionData } from 'components/question-builders/TextboxGridNumericQuestion';
import AnswerType from '@ax2/common/model/AnswerType';
import { RankingListQuestionData } from 'components/question-builders/RankingListQuestion';

export function getEmptyQuestionData(
  question: EmptyQuestionSchema,
  qChangeData: QuestionChangeData
): EmptyQuestionData {
  return {
    ...qChangeData,
    question,
    isScreener: false,
    isTypeDisabled: false,
  };
}

export function getTextQuestionData(
  question: TextQuestionSchema,
  isScreener: boolean,
  onChange: (s: TextQuestionSchema) => void,
  qChangeData: QuestionChangeData
): OpenEndedTextQuestionData | OpenEndedNumericQuestionData {
  const templateData = getTemplateData(question.content, c =>
    onChange({ ...question, content: c })
  );
  return {
    ...templateData,
    ...qChangeData,
    question,
    isScreener,
    isTypeDisabled: question.isLocked,
  };
}

export function getGridQuestionData(
  question: GridQuestionSchema,
  isScreener: boolean,
  onChange: (s: GridQuestionSchema) => void,
  qChangeData: QuestionChangeData
):
  | CheckboxGridQuestionData
  | RadioButtonGridQuestionData
  | RadioButtonGridRowQuestionData
  | TextboxGridNumericQuestionData {
  const templateData = getTemplateData(question.content, c =>
    onChange({ ...question, content: c })
  );
  const gridData = {
    columns: question.columns.map(col => ({
      ...col,
      ...getTemplateData(col.content, content => {
        const newCol = { ...col, content };
        const columns = replaceById(question.columns, newCol);
        onChange({ ...question, columns });
      }),
    })),
    rows: _.filter(question.rows, r => r.typ === AnswerType.Standard).map(
      row => ({
        ...row,
        ...getTemplateData(row.content, content => {
          const newRow = { ...row, content };
          const rows = replaceById(question.rows, newRow);
          onChange({ ...question, rows });
        }),
      })
    ),
    editable: true,
    isLocked: question.isLocked,
    showOrder: false,
    onNewColumn: () => {
      const colWithMaxOrder = _.maxBy(question.columns, 'order');
      const order = colWithMaxOrder ? colWithMaxOrder.order + 1 : 0;
      const newColumn = createAnswerSchema(null, order);
      const columns = add(question.columns, newColumn);
      onChange({ ...question, columns });
    },
    onNewRow: () => {
      const rowWithMaxOrder = _.maxBy(question.rows, 'order');
      const order = rowWithMaxOrder ? rowWithMaxOrder.order + 1 : 0;
      const newRow = createAnswerSchema(null, order);
      const rows = add(question.rows, newRow);
      onChange({ ...question, rows });
    },
    onChangeColumns: (_cols: GridOption[]) => {
      const columns = _.map(_cols, c => {
        const answerColumnSchema: AnswerColumnSchema | undefined = _.find(
          question.columns,
          sc => sc.id === c.id
        );
        return createAnswerSchema(
          c.id,
          c.order,
          answerColumnSchema ? answerColumnSchema.content : undefined,
          answerColumnSchema ? answerColumnSchema.typ : undefined
        );
      });
      onChange({ ...question, columns });
    },
    onChangeRows: (_rows: GridOption[]) => {
      const rows = _.map(_rows, r => {
        const answerRowSchema: AnswerRowSchema | undefined = _.find(
          question.rows,
          sr => sr.id === r.id
        );
        return createAnswerSchema(
          r.id,
          r.order,
          answerRowSchema ? answerRowSchema.content : undefined,
          answerRowSchema ? answerRowSchema.typ : undefined
        );
      });
      onChange({ ...question, rows });
    },
  };
  return {
    ...templateData,
    ...gridData,
    ...qChangeData,
    randomize: getGridRandomize(question, v => onChange(v)),
    qNAAnswer: getQuestionNAAnswerTypeData(question, v =>
      onChange(v as GridQuestionSchema)
    ),
    qPDAnswer: getQuestionPDAnswerTypeData(question, v =>
      onChange(v as GridQuestionSchema)
    ),
    question,
    isScreener,
    isTypeDisabled: question.isLocked,
  };
}

export function getListQuestionData(
  question: ListQuestionSchema,
  isScreener: boolean,
  onChange: (s: ListQuestionSchema) => void,
  qChangeData: QuestionChangeData
):
  | DropdownMenuQuestionData
  | CheckboxListQuestionData
  | RadioButtonListQuestionData
  | TextboxListTextQuestionData
  | TextboxListNumericQuestionData
  | RankingListQuestionData
  | TextboxListSumData {
  const templateData = getTemplateData(question.content, c =>
    onChange({ ...question, content: c })
  );
  const data = {
    options: _.filter(
      question.answerOptions,
      o => o.typ === AnswerType.Standard
    ).map(opt => ({
      ...opt,
      ...getTemplateData(opt.content, content => {
        const newOpt = { ...opt, content };
        const answerOptions = replaceById(question.answerOptions, newOpt);
        onChange({ ...question, answerOptions });
      }),
    })),
    editable: true,
    isLocked: question.isLocked,
    showOrder: false,
    onNew: () => {
      const optWithMaxOrder = _.maxBy(question.answerOptions, 'order');
      const order = optWithMaxOrder ? optWithMaxOrder.order + 1 : 0;
      const newOpt = createAnswerSchema(null, order);
      const answerOptions = add(question.answerOptions, newOpt);
      onChange({ ...question, answerOptions });
    },
    onChange: (opts: ListOption[]) => {
      const answerOptions = _.map(opts, o => {
        const answerOptSchema: AnswerRowSchema | undefined = _.find(
          question.answerOptions,
          so => so.id === o.id
        );
        return createAnswerSchema(
          o.id,
          o.order,
          answerOptSchema ? answerOptSchema.content : undefined,
          answerOptSchema ? answerOptSchema.typ : undefined
        );
      });
      onChange({ ...question, answerOptions });
    },
  };
  return {
    ...templateData,
    ...data,
    ...qChangeData,
    randomize: getListRandomize(question, v => onChange(v)),
    qNAAnswer: getQuestionNAAnswerTypeData(question, v =>
      onChange(v as ListQuestionSchema)
    ),
    qPDAnswer: getQuestionPDAnswerTypeData(question, v =>
      onChange(v as ListQuestionSchema)
    ),
    question,
    isScreener,
    isTypeDisabled: question.isLocked,
  };
}

export function getTextListQuestionData(
  question: ListQuestionSchema,
  isScreener: boolean,
  onChange: (s: ListQuestionSchema) => void,
  qChangeData: QuestionChangeData
): TextboxListTextQuestionData | TextboxListNumericQuestionData {
  const templateData = getTemplateData(question.content, c =>
    onChange({ ...question, content: c })
  );
  const data = {
    options: question.answerOptions.map(opt => ({
      ...opt,
      ...getTemplateData(opt.content, content => {
        const newOpt = { ...opt, content };
        const answerOptions = replaceById(question.answerOptions, newOpt);
        onChange({ ...question, answerOptions });
      }),
    })),
    editable: true,
    isLocked: question.isLocked,
    showOrder: false,
    onNew: () => {
      const optWithMaxOrder = _.maxBy(question.answerOptions, 'order');
      const order = optWithMaxOrder ? optWithMaxOrder.order + 1 : 0;
      const newOpt = createAnswerSchema(null, order);
      const answerOptions = add(question.answerOptions, newOpt);
      const requiredOptions = {
        minAmount: answerOptions.length,
      };
      onChange({ ...question, answerOptions, requiredOptions });
    },
    onChange: (opts: ListOption[]) => {
      const answerOptions = _.map(opts, o => {
        const answerOptSchema: AnswerRowSchema | undefined = _.find(
          question.answerOptions,
          so => so.id === o.id
        );
        return createAnswerSchema(
          o.id,
          o.order,
          answerOptSchema ? answerOptSchema.content : undefined,
          answerOptSchema ? answerOptSchema.typ : undefined
        );
      });
      const requiredOptions = {
        minAmount: answerOptions.length,
      };
      onChange({ ...question, answerOptions, requiredOptions });
    },
  };
  return {
    ...templateData,
    ...data,
    ...qChangeData,
    randomize: getListRandomize(question, v => onChange(v)),
    question,
    isScreener,
    isTypeDisabled: question.isLocked,
  };
}

export function getRatingListQuestionData(
  question: GridQuestionSchema,
  isScreener: boolean,
  onChange: (s: GridQuestionSchema) => void,
  qChangeData: QuestionChangeData
): RatingListQuestionData {
  const templateData = getTemplateData(question.content, c =>
    onChange({ ...question, content: c })
  );
  const data = {
    steps: question.columns.map(col => ({
      ...col,
      ...getTemplateData(col.content, content => {
        const neCol = { ...col, content };
        const columns = replaceById(question.columns, neCol);
        onChange({ ...question, columns });
      }),
    })),
    attributes: question.rows.map(row => ({
      ...row,
      ...getTemplateData(row.content, content => {
        const newRow = { ...row, content };
        const rows = replaceById(question.rows, newRow);
        onChange({ ...question, rows });
      }),
    })),
    isLocked: question.isLocked,
    isAttrsHidden:
      question.rows.length === 1 &&
      question.rows[0].content.template.length === 0, // For single rating without row text we dont need to show attributes
    onNewStep: () => {
      const colWithMaxOrder = _.maxBy(question.columns, 'order');
      const order = colWithMaxOrder ? colWithMaxOrder.order + 1 : 0;
      const newColumn = {
        ...createAnswerSchema(null, order),
        label: (order + 1).toString(),
      };
      const columns = add(question.columns, newColumn);
      onChange({ ...question, columns });
    },
    onNewAttr: () => {
      const rowWithMaxOrder = _.maxBy(question.rows, 'order');
      const order = rowWithMaxOrder ? rowWithMaxOrder.order + 1 : 0;
      const newRow = createAnswerSchema(null, order);
      const rows = add(question.rows, newRow);
      onChange({ ...question, rows });
    },
    onChangeSteps: (steps: StepOption[]) => {
      const columns = _.map(steps, c => {
        const answerColumnSchema: AnswerColumnSchema | undefined = _.find(
          question.columns,
          sc => sc.id === c.id
        );
        return createAnswerSchemaWithLabel(
          c.id,
          c.order,
          (c.order + 1).toString(),
          answerColumnSchema ? answerColumnSchema.content : undefined,
          answerColumnSchema ? answerColumnSchema.typ : undefined
        );
      });
      onChange({ ...question, columns });
    },
    onChangeAttrs: (attrs: AttrOption[]) => {
      const rows = _.map(attrs, r => {
        const answerRowSchema: AnswerRowSchema | undefined = _.find(
          question.rows,
          sr => sr.id === r.id
        );
        return createAnswerSchema(
          r.id,
          r.order,
          answerRowSchema ? answerRowSchema.content : undefined,
          answerRowSchema ? answerRowSchema.typ : undefined
        );
      });
      onChange({ ...question, rows });
    },
  };
  return {
    ...templateData,
    ...data,
    ...qChangeData,
    randomize: getGridRandomize(question, v => onChange(v)),
    question,
    isScreener,
    isTypeDisabled: question.isLocked,
  };
}

const getListRandomize = (
  question: ListQuestionSchema,
  onChange: (q: ListQuestionSchema) => void
): QuestionRandomizeData | undefined =>
  'randomize' in question &&
  question.questionType !== QuestionType.TextboxListText &&
  question.questionType !== QuestionType.TextboxListNumeric
    ? {
        disabled: question.isLocked,
        value: question.randomize,
        onChange: (v: boolean) => onChange({ ...question, randomize: v }),
      }
    : undefined;

const getGridRandomize = (
  question: GridQuestionSchema,
  onChange: (q: GridQuestionSchema) => void
): QuestionRandomizeData | undefined =>
  'randomizeRow' in question // TODO: add column support later (when it will be implement in takesurvey app)
    ? {
        disabled: question.isLocked,
        value: question.randomizeRow,
        onChange: (v: boolean) => onChange({ ...question, randomizeRow: v }),
      }
    : undefined;

const getQuestionPDAnswerTypeData = (
  schema: GridQuestionSchema | ListQuestionSchema,
  onChange: (q: GridQuestionSchema | ListQuestionSchema) => void
): QuestionPDAnswerTypeData => {
  const isList = 'answerOptions' in schema;
  const listSchema = schema as ListQuestionSchema;
  const gridSchema = schema as GridQuestionSchema;

  const options = isList ? listSchema.answerOptions : gridSchema.rows;
  const isSelected =
    _.findIndex(options, o => o.typ === AnswerType.ParticipantDefined) > -1;

  return {
    disabled: schema.isLocked,
    state: isSelected,
    onChangeState: (v: boolean) => {
      if (v) {
        const newOption = createAnswerSchema(
          null,
          98,
          {
            template: 'Other: [Specify]',
          },
          AnswerType.ParticipantDefined
        );
        const newOptions = options.concat([newOption]);
        onChange(
          isList
            ? { ...listSchema, answerOptions: newOptions }
            : { ...gridSchema, rows: newOptions }
        );
      } else {
        const newOptions = _.filter(
          options,
          o => o.typ !== AnswerType.ParticipantDefined
        );
        onChange(
          isList
            ? { ...listSchema, answerOptions: newOptions }
            : { ...gridSchema, rows: newOptions }
        );
      }
    },
  };
};

const getQuestionNAAnswerTypeData = (
  schema: GridQuestionSchema | ListQuestionSchema,
  onChange: (q: GridQuestionSchema | ListQuestionSchema) => void
): QuestionNAAnswerTypeData => {
  const isList = 'answerOptions' in schema;
  const listSchema = schema as ListQuestionSchema;
  const gridSchema = schema as GridQuestionSchema;

  const options = isList ? listSchema.answerOptions : gridSchema.rows;
  const naOption = _.find(options, o => o.typ === AnswerType.NA);

  return {
    disabled: schema.isLocked,
    state: !!naOption,
    templateData:
      naOption &&
      getTemplateData(naOption.content, content => {
        const newOption = { ...naOption, content };
        const newOptions = replaceById(options, newOption);
        onChange(
          isList
            ? { ...listSchema, answerOptions: newOptions }
            : { ...gridSchema, rows: newOptions }
        );
      }),
    onChangeState: (v: boolean) => {
      if (v) {
        const newOption = createAnswerSchema(
          null,
          99,
          {
            template: '%%0',
            groups: {
              0: {
                placeholder: 'Insert answer option label',
                text: 'Not applicable',
              },
            },
          },
          AnswerType.NA
        );
        const newOptions = options.concat([newOption]);
        onChange(
          isList
            ? { ...listSchema, answerOptions: newOptions }
            : { ...gridSchema, rows: newOptions }
        );
      } else {
        const newOptions = _.filter(options, o => o.typ !== AnswerType.NA);
        onChange(
          isList
            ? { ...listSchema, answerOptions: newOptions }
            : { ...gridSchema, rows: newOptions }
        );
      }
    },
  };
};
